/* eslint-disable no-useless-catch */
import { ENV } from "../utils";

export class AcademicPeriod {
  baseApi = ENV.BASE_API;

  /**
   * Fetches periods of a specific academic cycle.
   * @param {string} accessToken - JWT token
   * @param {number} academicCycleId - Academic cycle ID
   * @returns {Promise<Array>} - List of periods
   */
  async getPeriodsByCycle(accessToken, academicCycleId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/cycle/${academicCycleId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches the current active period of a cycle.
   * @param {string} accessToken - JWT token
   * @param {number} academicCycleId - Academic cycle ID
   * @returns {Promise<Object>} - Current period
   */
  async getCurrentPeriod(accessToken, academicCycleId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/cycle/${academicCycleId}/current`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches a period by ID.
   * @param {string} accessToken - JWT token
   * @param {number} periodId - Period ID
   * @returns {Promise<Object>} - Period object
   */
  async getPeriod(accessToken, periodId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/${periodId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates a period.
   * @param {string} accessToken - JWT token
   * @param {number} periodId - Period ID
   * @param {Object} data - { name, startDate, endDate }
   * @returns {Promise<Object>} - Updated period
   */
  async updatePeriod(accessToken, periodId, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/${periodId}`;
      const params = {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Closes a period.
   * @param {string} accessToken - JWT token
   * @param {number} periodId - Period ID
   * @returns {Promise<Object>} - Updated period
   */
  async closePeriod(accessToken, periodId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/${periodId}/close`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reopens a period.
   * @param {string} accessToken - JWT token
   * @param {number} periodId - Period ID
   * @returns {Promise<Object>} - Updated period
   */
  async reopenPeriod(accessToken, periodId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/${periodId}/reopen`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Deletes a period (soft delete).
   * @param {string} accessToken - JWT token
   * @param {number} periodId - Period ID
   * @returns {Promise<Object>} - Deletion result
   */
  async deletePeriod(accessToken, periodId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.ACADEMIC_PERIOD}/${periodId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }
}
